import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';

/**
 * Operator organization drill-down — a complete 2-pane view of everything the
 * platform knows about one tenant. Dedicated route /platform/organizations/:id
 * (accepts slug or UUID).
 *
 * Left pane  = sticky org identity, lifecycle controls, and a section rail.
 * Right pane = every section: overview aggregates, metadata, modules, domains,
 *              events (with per-event stats), venues, users (role management),
 *              and lifecycle workflows.
 */

interface TenantWorkflow {
  id: string;
  name: string;
  status: string;
  icon: string;
  category?: { name: string };
  steps: { status: string }[];
}

interface TenantUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  mustChangePassword: boolean;
  createdAt: string;
}

interface TenantEventRow {
  id: string;
  name: string;
  date: string | null;
  status: string;
  venueName: string | null;
  guestCount: number;
  ticketCount: number;
  revenue: number;
}

interface TenantVenueRow {
  id: string;
  name: string;
  city: string;
  state: string;
  capacity: number;
  eventCount: number;
}

interface TenantAggregates {
  events: number;
  activeEvents: number;
  venues: number;
  users: number;
  guests: number;
  tickets: number;
  orders: number;
  revenue: number;
  seats: number;
  assignedSeats: number;
  checkedIn: number;
}

interface TenantAccountMetadata {
  planTier: string | null;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  billingEmail: string | null;
  notes: string | null;
}

interface TenantDetail {
  id: string;
  slug: string;
  name: string;
  status: string;
  enabledModules: string[];
  createdAt: string;
  updatedAt: string;
  hubUrl: string;
  customDomains: { host: string; verified: boolean }[];
  metadata: TenantAccountMetadata;
  eventCount: number;
  userCount: number;
  users: TenantUser[];
  events: TenantEventRow[];
  venues: TenantVenueRow[];
  aggregates: TenantAggregates;
}

const EMPTY_META: TenantAccountMetadata = {
  planTier: '', primaryContactName: '', primaryContactEmail: '',
  primaryContactPhone: '', billingEmail: '', notes: '',
};

const ROLE_LABEL: Record<string, string> = {
  CustomerSuperUser: 'Super User',
  CustomerEditor: 'Editor',
  CustomerUser: 'User',
  Organizer: 'Super User (legacy)',
};

const ASSIGNABLE_ROLES: { value: string; label: string }[] = [
  { value: 'CustomerSuperUser', label: 'Super User' },
  { value: 'CustomerEditor', label: 'Editor' },
  { value: 'CustomerUser', label: 'User' },
];

const MODULE_META: Record<string, { icon: string; label: string }> = {
  guests: { icon: '🎟️', label: 'Guest List' },
  ticketing: { icon: '💳', label: 'Ticketing' },
  seating: { icon: '🪑', label: 'Seating' },
  checkin: { icon: '✅', label: 'Check-In' },
  agenda: { icon: '📅', label: 'Agenda' },
  vendors: { icon: '🏪', label: 'Vendors' },
  comms: { icon: '📢', label: 'Comms' },
  website: { icon: '🌐', label: 'Experience' },
  giving: { icon: '💚', label: 'Giving' },
};

const SECTIONS: { key: string; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: '📊' },
  { key: 'metadata', label: 'Metadata', icon: '🧾' },
  { key: 'modules', label: 'Modules', icon: '🧩' },
  { key: 'events', label: 'Events', icon: '📅' },
  { key: 'venues', label: 'Venues', icon: '📍' },
  { key: 'users', label: 'Users', icon: '👥' },
  { key: 'workflows', label: 'Workflows', icon: '📋' },
];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso.split('T')[0]; }
}
function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function statusStyle(status: string): React.CSSProperties {
  if (status === 'Active' || status === 'Live') return { background: '#dcfce7', color: '#166534' };
  if (status === 'Draft') return { background: '#fef3c7', color: '#92400e' };
  return { background: '#f1f5f9', color: '#475569' };
}

export function PlatformTenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [workflows, setWorkflows] = useState<TenantWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);
  const [roleBusyUserId, setRoleBusyUserId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState<TenantAccountMetadata>(EMPTY_META);
  const [metaSaving, setMetaSaving] = useState(false);
  // Inline rename of the organization name.
  const [editingName, setEditingName] = useState(false);
  const [nameForm, setNameForm] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  async function load() {
    try {
      const [d, wfs] = await Promise.all([
        apiClient<TenantDetail>(`/platform/tenants/${id}`),
        apiClient<TenantWorkflow[]>(`/platform/workflows?tenantId=${id}`).catch(() => []),
      ]);
      setDetail(d);
      setWorkflows(Array.isArray(wfs) ? wfs : []);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function changeStatus(next: 'Active' | 'Suspended') {
    if (!detail) return;
    setStatusBusy(true);
    setError('');
    try {
      const updated = await apiClient<TenantDetail>(`/platform/tenants/${detail.id}/status`, {
        method: 'PATCH', body: { status: next },
      });
      setDetail(updated);
    } catch (e: any) {
      setError(e?.message || 'Failed to update status');
    } finally {
      setStatusBusy(false);
    }
  }

  async function changeUserRole(userId: string, role: string) {
    if (!detail) return;
    setRoleBusyUserId(userId);
    setError('');
    try {
      const updated = await apiClient<TenantDetail>(`/platform/tenants/${detail.id}/users/${userId}/role`, {
        method: 'PATCH', body: { role },
      });
      setDetail(updated);
    } catch (e: any) {
      setError(e?.message || 'Failed to update user role');
    } finally {
      setRoleBusyUserId(null);
    }
  }

  function startEditMeta() {
    if (!detail) return;
    const m = detail.metadata;
    setMetaForm({
      planTier: m.planTier ?? '',
      primaryContactName: m.primaryContactName ?? '',
      primaryContactEmail: m.primaryContactEmail ?? '',
      primaryContactPhone: m.primaryContactPhone ?? '',
      billingEmail: m.billingEmail ?? '',
      notes: m.notes ?? '',
    });
    setEditingMeta(true);
  }

  async function saveMeta(e: React.FormEvent) {
    e.preventDefault();
    if (!detail) return;
    setMetaSaving(true);
    setError('');
    try {
      const updated = await apiClient<TenantDetail>(`/platform/tenants/${detail.id}/metadata`, {
        method: 'PATCH', body: metaForm,
      });
      setDetail(updated);
      setEditingMeta(false);
    } catch (e2: any) {
      setError(e2?.message || 'Failed to save metadata');
    } finally {
      setMetaSaving(false);
    }
  }

  function startEditName() {
    if (!detail) return;
    setNameForm(detail.name);
    setEditingName(true);
    setError('');
  }

  async function saveName() {
    if (!detail) return;
    const trimmed = nameForm.trim();
    if (!trimmed) { setError('Name must not be empty.'); return; }
    if (trimmed === detail.name) { setEditingName(false); return; }
    setNameSaving(true);
    setError('');
    try {
      const updated = await apiClient<TenantDetail>(`/platform/tenants/${detail.id}/name`, {
        method: 'PATCH', body: { name: trimmed },
      });
      setDetail(updated);
      setEditingName(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to rename organization');
    } finally {
      setNameSaving(false);
    }
  }

  function scrollToSection(key: string) {
    setActiveSection(key);
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (loading) {
    return <div style={styles.wrap}><Link to="/platform/organizations" style={styles.back}>← All organizations</Link><div style={styles.muted}>Loading organization…</div></div>;
  }
  if (error && !detail) {
    return <div style={styles.wrap}><Link to="/platform/organizations" style={styles.back}>← All organizations</Link><div style={styles.error}>{error}</div></div>;
  }
  if (!detail) return null;

  const a = detail.aggregates;
  const seatedPct = a.seats > 0 ? Math.round((a.assignedSeats / a.seats) * 100) : 0;

  return (
    <div style={styles.wrap}>
      <style>{`
        @media (max-width: 900px) { .ep-org-2pane { grid-template-columns: 1fr !important; } .ep-org-left { position: static !important; } }
      `}</style>

      <Link to="/platform/organizations" style={styles.back}>← All organizations</Link>
      {error && <div style={styles.error} onClick={() => setError('')}>{error}</div>}

      <div style={styles.twoPane} className="ep-org-2pane">
        {/* ── Left pane: identity + controls + section rail ── */}
        <aside style={styles.left} className="ep-org-left">
          <div style={styles.idCard}>
            <div style={styles.idGlow} aria-hidden />
            <div style={styles.idKicker}>Organization</div>
            {editingName ? (
              <div style={{ position: 'relative', zIndex: 1, marginTop: 6 }}>
                <input
                  style={styles.nameInput}
                  value={nameForm}
                  onChange={(e) => setNameForm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                  aria-label="Organization name"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button style={styles.nameSaveBtn} onClick={saveName} disabled={nameSaving}>
                    {nameSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button style={styles.nameCancelBtn} onClick={() => setEditingName(false)} disabled={nameSaving}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <h1 style={styles.idName}>
                {detail.name}
                <button
                  style={styles.nameEditBtn}
                  onClick={startEditName}
                  title="Rename organization"
                  aria-label="Rename organization"
                >
                  ✏️
                </button>
              </h1>
            )}
            <code style={styles.idSlug}>/{detail.slug}</code>
            <div style={{ marginTop: 12 }}>
              <span style={{ ...styles.pill, ...statusStyle(detail.status) }}>{detail.status}</span>
            </div>
            <a
              href={`http://${detail.hubUrl}`}
              target="_blank"
              rel="noreferrer"
              style={{ ...styles.hubBtn, ...(detail.status !== 'Active' ? styles.hubBtnDisabled : {}) }}
              onClick={(e) => { if (detail.status !== 'Active') e.preventDefault(); }}
            >
              Open hub ↗
            </a>
            <div style={styles.lifecycle}>
              {detail.status === 'Active' ? (
                <button style={styles.suspendBtn} onClick={() => changeStatus('Suspended')} disabled={statusBusy}>
                  {statusBusy ? 'Working…' : 'Suspend'}
                </button>
              ) : (
                <button style={styles.activateBtn} onClick={() => changeStatus('Active')} disabled={statusBusy}>
                  {statusBusy ? 'Working…' : 'Reactivate'}
                </button>
              )}
            </div>
          </div>

          <nav style={styles.rail} aria-label="Organization sections">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                style={{ ...styles.railItem, ...(activeSection === s.key ? styles.railItemActive : {}) }}
                onClick={() => scrollToSection(s.key)}
                aria-current={activeSection === s.key ? 'true' : undefined}
              >
                <span aria-hidden>{s.icon}</span> {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* ── Right pane: all sections ── */}
        <div style={styles.right}>
          {/* Overview */}
          <section ref={(el) => { sectionRefs.current.overview = el; }} style={styles.section}>
            <h2 style={styles.h2}>📊 Overview</h2>
            <div style={styles.statGrid}>
              <Stat label="Events" value={`${a.events}`} sub={`${a.activeEvents} active`} />
              <Stat label="Venues" value={`${a.venues}`} />
              <Stat label="Users" value={`${a.users}`} />
              <Stat label="Guests" value={a.guests.toLocaleString()} />
              <Stat label="Orders" value={a.orders.toLocaleString()} />
              <Stat label="Active tickets" value={a.tickets.toLocaleString()} />
              <Stat label="Revenue" value={money(a.revenue)} accent />
              <Stat label="Seated" value={`${seatedPct}%`} sub={`${a.assignedSeats}/${a.seats}`} />
              <Stat label="Checked in" value={a.checkedIn.toLocaleString()} />
            </div>
          </section>

          {/* Metadata */}
          <section ref={(el) => { sectionRefs.current.metadata = el; }} style={styles.section}>
            <div style={styles.sectionHead}>
              <h2 style={styles.h2}>🧾 Metadata</h2>
              {!editingMeta && (
                <button style={styles.editBtn} onClick={startEditMeta}>✏️ Edit account</button>
              )}
            </div>

            {/* System metadata (read-only) */}
            <div style={styles.metaGrid}>
              <Meta label="Organization ID" value={detail.id} mono />
              <Meta label="Slug" value={detail.slug} mono />
              <Meta label="Status" value={detail.status} />
              <Meta label="Hub address" value={detail.hubUrl} mono />
              <Meta label="Created" value={fmtDate(detail.createdAt)} />
              <Meta label="Last updated" value={fmtDate(detail.updatedAt)} />
              <Meta label="Enabled modules" value={`${detail.enabledModules.length}`} />
              <Meta label="Custom domains" value={`${detail.customDomains.length}`} />
            </div>

            {detail.customDomains.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={styles.subLabel}>Custom domains</div>
                {detail.customDomains.map((d) => (
                  <div key={d.host} style={styles.domainRow}>
                    <code style={styles.domainCode}>{d.host}</code>
                    <span style={d.verified ? styles.verified : styles.unverified}>
                      {d.verified ? '✓ verified' : 'unverified'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Account metadata — operator-managed */}
            <div style={{ marginTop: 18, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
              <div style={styles.subLabel}>Account</div>
              {editingMeta ? (
                <form onSubmit={saveMeta} style={styles.metaForm}>
                  <div style={styles.metaFormRow}>
                    <label style={styles.fLabel}>Plan tier
                      <input style={styles.fInput} value={metaForm.planTier ?? ''} onChange={(e) => setMetaForm({ ...metaForm, planTier: e.target.value })} placeholder="Free / Pro / Enterprise" />
                    </label>
                    <label style={styles.fLabel}>Billing email
                      <input style={styles.fInput} type="email" value={metaForm.billingEmail ?? ''} onChange={(e) => setMetaForm({ ...metaForm, billingEmail: e.target.value })} placeholder="billing@org.com" />
                    </label>
                  </div>
                  <label style={styles.fLabel}>Primary contact name
                    <input style={styles.fInput} value={metaForm.primaryContactName ?? ''} onChange={(e) => setMetaForm({ ...metaForm, primaryContactName: e.target.value })} />
                  </label>
                  <div style={styles.metaFormRow}>
                    <label style={styles.fLabel}>Contact email
                      <input style={styles.fInput} type="email" value={metaForm.primaryContactEmail ?? ''} onChange={(e) => setMetaForm({ ...metaForm, primaryContactEmail: e.target.value })} />
                    </label>
                    <label style={styles.fLabel}>Contact phone
                      <input style={styles.fInput} value={metaForm.primaryContactPhone ?? ''} onChange={(e) => setMetaForm({ ...metaForm, primaryContactPhone: e.target.value })} />
                    </label>
                  </div>
                  <label style={styles.fLabel}>Internal notes
                    <textarea style={{ ...styles.fInput, minHeight: 72, resize: 'vertical' }} value={metaForm.notes ?? ''} onChange={(e) => setMetaForm({ ...metaForm, notes: e.target.value })} placeholder="Operator-only notes about this organization" />
                  </label>
                  <div style={styles.metaFormActions}>
                    <button type="button" style={styles.cancelBtn} onClick={() => setEditingMeta(false)} disabled={metaSaving}>Cancel</button>
                    <button type="submit" style={styles.saveBtn} disabled={metaSaving}>{metaSaving ? 'Saving…' : 'Save account'}</button>
                  </div>
                </form>
              ) : (
                <div style={styles.metaGrid}>
                  <Meta label="Plan tier" value={detail.metadata.planTier || '—'} />
                  <Meta label="Billing email" value={detail.metadata.billingEmail || '—'} />
                  <Meta label="Primary contact" value={detail.metadata.primaryContactName || '—'} />
                  <Meta label="Contact email" value={detail.metadata.primaryContactEmail || '—'} />
                  <Meta label="Contact phone" value={detail.metadata.primaryContactPhone || '—'} />
                  <Meta label="Internal notes" value={detail.metadata.notes || '—'} />
                </div>
              )}
            </div>
          </section>

          {/* Modules */}
          <section ref={(el) => { sectionRefs.current.modules = el; }} style={styles.section}>
            <h2 style={styles.h2}>🧩 Modules</h2>
            {detail.enabledModules.length === 0 ? (
              <div style={styles.muted}>No modules enabled.</div>
            ) : (
              <div style={styles.chipWrap}>
                {detail.enabledModules.map((m) => {
                  const meta = MODULE_META[m] || { icon: '🧩', label: m };
                  return <span key={m} style={styles.moduleChip}>{meta.icon} {meta.label}</span>;
                })}
              </div>
            )}
          </section>

          {/* Events */}
          <section ref={(el) => { sectionRefs.current.events = el; }} style={styles.section}>
            <div style={styles.sectionHead}>
              <h2 style={styles.h2}>📅 Events ({detail.events.length})</h2>
              <span style={styles.cta} onClick={() => navigate('/platform/events')}>All events →</span>
            </div>
            {detail.events.length === 0 ? (
              <div style={styles.muted}>No events yet.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Event</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Venue</th>
                    <th style={styles.thNum}>Guests</th>
                    <th style={styles.thNum}>Tickets</th>
                    <th style={styles.thNum}>Revenue</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.events.map((e) => (
                    <tr key={e.id}>
                      <td style={styles.tdStrong}>{e.name}</td>
                      <td style={styles.td}>{fmtDate(e.date)}</td>
                      <td style={styles.td}>{e.venueName || '—'}</td>
                      <td style={styles.tdNum}>{e.guestCount}</td>
                      <td style={styles.tdNum}>{e.ticketCount}</td>
                      <td style={styles.tdNum}>{money(e.revenue)}</td>
                      <td style={styles.td}><span style={{ ...styles.pill, ...statusStyle(e.status) }}>{e.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Venues */}
          <section ref={(el) => { sectionRefs.current.venues = el; }} style={styles.section}>
            <div style={styles.sectionHead}>
              <h2 style={styles.h2}>📍 Venues ({detail.venues.length})</h2>
              <span style={styles.cta} onClick={() => navigate('/platform/venues')}>Manage venues →</span>
            </div>
            {detail.venues.length === 0 ? (
              <div style={styles.muted}>No venues yet.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Venue</th>
                    <th style={styles.th}>Location</th>
                    <th style={styles.thNum}>Capacity</th>
                    <th style={styles.thNum}>Events</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.venues.map((v) => (
                    <tr key={v.id}>
                      <td style={styles.tdStrong}>{v.name}</td>
                      <td style={styles.td}>{[v.city, v.state].filter(Boolean).join(', ') || '—'}</td>
                      <td style={styles.tdNum}>{v.capacity.toLocaleString()}</td>
                      <td style={styles.tdNum}>{v.eventCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Users */}
          <section ref={(el) => { sectionRefs.current.users = el; }} style={styles.section}>
            <h2 style={styles.h2}>👥 Users ({detail.users.length})</h2>
            {detail.users.length === 0 ? (
              <div style={styles.muted}>No users yet.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name / Email</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.users.map((u) => (
                    <tr key={u.id}>
                      <td style={styles.td}>
                        <div style={styles.tdStrong}>{u.fullName}</div>
                        <div style={styles.uEmail}>{u.email}</div>
                      </td>
                      <td style={styles.td}>
                        <select
                          style={styles.roleSelect}
                          value={ASSIGNABLE_ROLES.some((r) => r.value === u.role) ? u.role : ''}
                          disabled={roleBusyUserId === u.id}
                          onChange={(e) => changeUserRole(u.id, e.target.value)}
                          aria-label={`Role for ${u.fullName || u.email}`}
                        >
                          {!ASSIGNABLE_ROLES.some((r) => r.value === u.role) && (
                            <option value="" disabled>{ROLE_LABEL[u.role] || u.role}</option>
                          )}
                          {ASSIGNABLE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        {roleBusyUserId === u.id && <span style={styles.savingNote}> saving…</span>}
                        {u.mustChangePassword && <div style={styles.pwNote}>temp password</div>}
                      </td>
                      <td style={styles.td}>{fmtDate(u.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Workflows */}
          <section ref={(el) => { sectionRefs.current.workflows = el; }} style={styles.section}>
            <div style={styles.sectionHead}>
              <h2 style={styles.h2}>📋 Workflows ({workflows.length})</h2>
              <Link to="/platform/workflows" style={styles.cta}>Manage →</Link>
            </div>
            {workflows.length === 0 ? (
              <div style={styles.muted}>No workflows yet.</div>
            ) : (
              <div>
                {workflows.map((w) => {
                  const total = w.steps.length;
                  const done = w.steps.filter((s) => s.status === 'completed').length;
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  return (
                    <button key={w.id} style={styles.wfRow} onClick={() => navigate('/platform/workflows')}>
                      <span style={styles.wfIcon}>{w.icon}</span>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={styles.tdStrong}>{w.name}</div>
                        <div style={styles.uEmail}>{w.category?.name} · {done}/{total} steps · {pct}%</div>
                      </div>
                      <span style={{ ...styles.pill, ...statusStyle(w.status === 'completed' ? 'Live' : w.status === 'active' ? 'Draft' : '') }}>{w.status}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div style={styles.stat}>
      <div style={{ ...styles.statValue, ...(accent ? { color: '#4f46e5' } : {}) }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={styles.meta}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={{ ...styles.metaValue, ...(mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : {}) }}>{value}</div>
    </div>
  );
}

const ACCENT = '#4f46e5';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  wrap: {},
  back: { color: ACCENT, fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none', display: 'inline-block', marginBottom: 14 },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 16, cursor: 'pointer' },
  muted: { color: MUTED, padding: '16px 4px' },

  twoPane: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' },

  left: { position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 14 },
  idCard: { position: 'relative', overflow: 'hidden', borderRadius: 18, padding: 20, background: 'linear-gradient(135deg, #0b1120 0%, #312e81 100%)', color: '#fff' },
  idGlow: { position: 'absolute', top: -60, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.5), transparent 70%)', pointerEvents: 'none' },
  idKicker: { fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#a5b4fc', position: 'relative', zIndex: 1 },
  idName: { fontSize: '1.4rem', fontWeight: 800, margin: '6px 0 4px', letterSpacing: '-0.02em', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 8 },
  nameEditBtn: { background: 'rgba(255,255,255,0.14)', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', padding: '2px 6px', lineHeight: 1 },
  nameInput: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, fontSize: '1.1rem', fontWeight: 700, background: 'rgba(255,255,255,0.1)', color: '#fff' },
  nameSaveBtn: { background: '#059669', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' },
  nameCancelBtn: { background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 7, padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  idSlug: { fontSize: '0.8rem', color: '#c7d2fe', position: 'relative', zIndex: 1 },
  hubBtn: { display: 'block', textAlign: 'center', marginTop: 16, background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '8px 0', fontSize: '0.82rem', fontWeight: 700, textDecoration: 'none', position: 'relative', zIndex: 1 },
  hubBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  lifecycle: { marginTop: 10, position: 'relative', zIndex: 1 },
  suspendBtn: { width: '100%', background: 'rgba(239,68,68,0.15)', color: '#fecaca', border: '1px solid rgba(248,113,113,0.5)', borderRadius: 8, padding: '8px 0', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },
  activateBtn: { width: '100%', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 0', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },

  rail: { display: 'flex', flexDirection: 'column', gap: 2, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 8 },
  railItem: { textAlign: 'left', background: 'transparent', border: 'none', borderRadius: 9, padding: '9px 12px', fontSize: '0.86rem', fontWeight: 600, color: '#334155', cursor: 'pointer' },
  railItemActive: { background: '#eef2ff', color: ACCENT, fontWeight: 800 },

  right: { display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 },
  section: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)', scrollMarginTop: 16 },
  sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  h2: { fontSize: '1.05rem', fontWeight: 800, margin: '0 0 12px', color: INK },
  cta: { color: ACCENT, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'none' },

  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 },
  stat: { background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 12, padding: '12px 14px' },
  statValue: { fontSize: '1.5rem', fontWeight: 800, color: INK, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  statLabel: { fontSize: '0.72rem', color: MUTED, fontWeight: 700, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
  statSub: { fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 },

  metaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 },
  meta: { borderLeft: `3px solid ${BORDER}`, paddingLeft: 10 },
  metaLabel: { fontSize: '0.7rem', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' },
  metaValue: { fontSize: '0.9rem', color: INK, fontWeight: 600, marginTop: 2, wordBreak: 'break-all' },
  subLabel: { fontSize: '0.72rem', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 },
  domainRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  domainCode: { background: '#f1f5f9', borderRadius: 6, padding: '3px 8px', fontSize: '0.82rem' },
  verified: { color: '#047857', fontWeight: 700, fontSize: '0.78rem' },
  unverified: { color: '#b45309', fontWeight: 700, fontSize: '0.78rem' },

  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  moduleChip: { background: '#eef2ff', color: ACCENT, border: '1px solid #c7d2fe', borderRadius: 999, padding: '5px 12px', fontSize: '0.82rem', fontWeight: 700 },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  thNum: { textAlign: 'right', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' },
  td: { padding: '10px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top', color: '#334155' },
  tdStrong: { padding: '10px 10px 0', fontWeight: 700, color: INK },
  tdNum: { padding: '10px', borderBottom: '1px solid #f0f1f5', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#334155' },
  uEmail: { color: MUTED, fontSize: '0.78rem', padding: '0 10px 0' },
  roleSelect: { background: '#fff', color: INK, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '4px 8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  savingNote: { fontSize: '0.72rem', color: MUTED },
  pwNote: { color: '#b45309', fontSize: '0.72rem', marginTop: 4 },

  pill: { display: 'inline-block', borderRadius: 999, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 },

  wfRow: { width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, cursor: 'pointer' },
  wfIcon: { fontSize: '1.3rem' },

  editBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '5px 11px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  metaForm: { display: 'flex', flexDirection: 'column', gap: 10 },
  metaFormRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  fLabel: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 180, fontSize: '0.7rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.03em' },
  fInput: { padding: '8px 10px', border: `1px solid #cbd5e1`, borderRadius: 8, fontSize: '0.9rem', fontWeight: 400, color: INK, textTransform: 'none', letterSpacing: 'normal', boxSizing: 'border-box', width: '100%' },
  metaFormActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 },
  cancelBtn: { background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  saveBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' },
};
